import { ArrowsOutSimpleIcon, DotIcon } from '@phosphor-icons/react';
import { Button, Loader, Search, VText } from '@vake/ui-kit';
import React, { useEffect, useState } from 'react';
import { useSendToMattermost } from '../requests/reports';
import { SearchResult, useVesselNameSearch } from '../requests/search';
import { mmsiToFlag } from '../utils/mmsi-to-flag';
import { SearchPopup } from './search-popup';

const SearchComponent: React.FC = () => {
    const [searchInput, setSearchInput] = useState('');
    const { data, isLoading, isError, refetch, fetchNextPage, hasNextPage, isFetchingNextPage } =
        useVesselNameSearch(searchInput);
    const [showPopup, setShowPopup] = useState<boolean>(false);
    const [activeResult, setActiveResult] = useState<SearchResult | null>(null);
    const { mutate } = useSendToMattermost();

    const searchResults = data?.pages.flatMap((page) => page.data) || [];

    useEffect(() => {
        document.title = 'Search';
    }, []);

    useEffect(() => {
        const delay = 300;
        const timeoutId = setTimeout(() => {
            refetch();
        }, delay);

        return () => clearTimeout(timeoutId);
    }, [searchInput, refetch]);

    const handleOnClick = (result: SearchResult): void => {
        mutate(
            `In SearchComponent: User clicked on search result ${result.name} (MMSI: ${result.mmsi}, IMO: ${result.imo}, createdAt: ${result.createdAt})`
        );
        setShowPopup(true);
        setActiveResult(result);
    };

    if (isError) {
        return (
            <div className="flex items-center justify-center h-screen w-fill bg-background-primary">
                <VText>Fetching data failed</VText>
            </div>
        );
    }
    return (
        <>
            <SearchPopup activeResult={activeResult} showPopup={showPopup} setShowPopup={setShowPopup} />
            <div className="flex flex-col items-center text-center mt-24 gap-1 h-[calc(100vh-12rem)]">
                <div className="min-w-72">
                    <Search
                        placeholder={'Search for Name, MMSI or IMO'}
                        query={searchInput}
                        handleChange={setSearchInput}
                        regexFilterOut={/[^a-zA-Z0-9 ]/g}
                        focusOnMount={true}
                    />
                </div>
                {searchInput.length > 0 && <VText>Flag - Name - MMSI - IMO - Change Registered</VText>}
                {isLoading && <Loader size={24} />}
                <div className="flex flex-col overflow-y-auto">
                    {searchResults.map((result, i) => {
                        return (
                            <button
                                className="p-2 border rounded flex items-center hover:bg-background-primary-hover"
                                key={i}
                                onClick={() => handleOnClick(result)}
                            >
                                <VText>{mmsiToFlag(result.mmsi)}</VText>
                                <VText>
                                    <DotIcon />
                                </VText>
                                <VText>{result.name}</VText>
                                <VText>
                                    <DotIcon />
                                </VText>
                                <VText>{result.mmsi}</VText>
                                <VText>
                                    <DotIcon />
                                </VText>
                                <VText>{result.imo}</VText>
                                <VText>
                                    <DotIcon />
                                </VText>
                                <VText>{result.createdAt.toISOString().slice(0, 10)}</VText>
                                <div className="px-2">
                                    <ArrowsOutSimpleIcon size={16} color="white" />
                                </div>
                            </button>
                        );
                    })}
                </div>
                {searchResults.length > 0 && (
                    <>
                        {isFetchingNextPage && <Loader size={16} />}
                        {!isFetchingNextPage && hasNextPage && (
                            <Button
                                onClick={() => {
                                    fetchNextPage();
                                    mutate(`In SearchComponent: User clicked load more for search term ${searchInput}`);
                                }}
                                disabled={isFetchingNextPage}
                                text="Load more"
                                variant="neutral"
                            />
                        )}
                    </>
                )}
                {searchInput.length > 0 && searchResults.length === 0 && !isLoading && (
                    <VText>No results found for the given search term.</VText>
                )}
            </div>
        </>
    );
};

export default SearchComponent;
